// @ts-nocheck
/**
 * 市場分析画面
 *
 * 詳細な市場データの分析・可視化
 * - MSI（Magic Spirit Index）総合指数
 * - 属性別トレンド
 * - カテゴリランキング
 * - ティア別分析
 * - イベント履歴
 * - 個別カード検索
 */

import React, { useState, useMemo } from 'react';
import { useIsMobile } from '../hooks/useMediaQuery';
import { ATTRIBUTES } from '../market/constants';
import { CATEGORIES, CATEGORY_ATTRIBUTES } from '../market/data/categories';
import {
  getMarketIndexChartData,
  getAttributeChartData,
  getCategoryChartData,
  getTierChartData,
  getCardChartData,
  getTrendIcon,
  getTrendColor,
  generateSparklineData,
} from '../market/priceHistory';
import { PriceChart, MiniChart, MarketIndexDisplay } from './PriceChart';
import {
  getAssetChartData,
  getAssetBreakdown,
  getAssetModeLabel,
} from '../systems/assetCalculator';

// ========================================
// 定数
// ========================================

const TIERS = ['S', 'A', 'B', 'C', 'D'];

const TABS = [
  { id: 'overview', label: '総合', icon: '📊' },
  { id: 'persistent', label: '永続', icon: '📈' },
  { id: 'attributes', label: '属性', icon: '🔮' },
  { id: 'categories', label: 'カテゴリ', icon: '🏷️' },
  { id: 'tiers', label: 'ティア', icon: '⭐' },
  { id: 'assets', label: '資産', icon: '💰' },
  { id: 'events', label: 'イベント', icon: '📰' },
  { id: 'search', label: '検索', icon: '🔍' },
];

const ATTRIBUTE_COLORS = {
  '炎': '#ff6b6b',
  '水': '#4dabf7',
  '光': '#ffd43b',
  '闇': '#9775fa',
  '原始': '#69db7c',
  '未来': '#74c0fc',
  'なし': '#868e96',
};

const TIER_COLORS = {
  'S': '#ffd700',
  'A': '#c0c0c0',
  'B': '#cd7f32',
  'C': '#69db7c',
  'D': '#868e96',
};

// ========================================
// スタイル
// ========================================

const createStyles = (isMobile) => ({
  container: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: isMobile ? '12px 16px' : '16px 24px',
    borderBottom: '1px solid #333',
    backgroundColor: '#1a1a2e',
    flexShrink: 0,
  },
  title: {
    fontSize: isMobile ? '18px' : '24px',
    fontWeight: 'bold',
    color: '#ffd700',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    color: '#fff',
    fontSize: '24px',
    cursor: 'pointer',
    padding: '4px 8px',
  },
  tabBar: {
    display: 'flex',
    gap: isMobile ? '4px' : '8px',
    padding: isMobile ? '8px 12px' : '12px 24px',
    backgroundColor: '#1a1a2e',
    borderBottom: '1px solid #333',
    overflowX: 'auto',
    flexShrink: 0,
  },
  tab: {
    padding: isMobile ? '8px 12px' : '10px 16px',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: isMobile ? '12px' : '14px',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    whiteSpace: 'nowrap',
    transition: 'all 0.2s',
  },
  tabActive: {
    backgroundColor: '#ffd700',
    color: '#000',
  },
  tabInactive: {
    backgroundColor: '#333',
    color: '#ccc',
  },
  content: {
    flex: 1,
    overflow: 'auto',
    padding: isMobile ? '16px' : '24px',
  },
  contentInner: {
    maxWidth: '1200px',
    margin: '0 auto',
  },
  section: {
    marginBottom: isMobile ? '20px' : '32px',
  },
  sectionTitle: {
    fontSize: isMobile ? '16px' : '20px',
    fontWeight: 'bold',
    color: '#fff',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: isMobile ? '12px' : '16px',
  },
  card: {
    backgroundColor: '#252540',
    borderRadius: '12px',
    padding: isMobile ? '12px' : '16px',
    border: '1px solid #333',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  cardTitle: {
    fontSize: isMobile ? '14px' : '16px',
    fontWeight: 'bold',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  badge: {
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 'bold',
  },
  statRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
    borderBottom: '1px solid #333',
  },
  statLabel: {
    color: '#888',
    fontSize: isMobile ? '12px' : '14px',
  },
  statValue: {
    color: '#fff',
    fontSize: isMobile ? '14px' : '16px',
    fontWeight: 'bold',
  },
  sparkline: {
    display: 'flex',
    alignItems: 'flex-end',
    height: '40px',
    gap: '2px',
  },
  sparklineBar: {
    width: isMobile ? '8px' : '12px',
    backgroundColor: '#4dabf7',
    borderRadius: '2px 2px 0 0',
    transition: 'height 0.3s',
  },
  searchInput: {
    width: '100%',
    padding: isMobile ? '10px 12px' : '12px 16px',
    fontSize: isMobile ? '14px' : '16px',
    border: '2px solid #333',
    borderRadius: '8px',
    backgroundColor: '#1a1a2e',
    color: '#fff',
    outline: 'none',
    marginBottom: '16px',
  },
  searchResults: {
    maxHeight: '400px',
    overflow: 'auto',
  },
  searchResultItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px',
    backgroundColor: '#252540',
    borderRadius: '8px',
    marginBottom: '8px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  eventList: {
    maxHeight: isMobile ? '300px' : '400px',
    overflow: 'auto',
  },
  eventItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '12px',
    backgroundColor: '#252540',
    borderRadius: '8px',
    marginBottom: '8px',
  },
  eventIcon: {
    fontSize: '24px',
    flexShrink: 0,
  },
  eventContent: {
    flex: 1,
  },
  eventType: {
    fontSize: '12px',
    color: '#888',
    marginBottom: '4px',
  },
  eventText: {
    fontSize: isMobile ? '13px' : '14px',
    color: '#fff',
  },
  noData: {
    textAlign: 'center',
    padding: '40px',
    color: '#666',
    fontSize: isMobile ? '14px' : '16px',
  },
  msiDisplay: {
    backgroundColor: '#1a1a2e',
    borderRadius: '16px',
    padding: isMobile ? '16px' : '24px',
    marginBottom: '24px',
    border: '2px solid #ffd700',
  },
  msiValue: {
    fontSize: isMobile ? '36px' : '48px',
    fontWeight: 'bold',
    color: '#ffd700',
    textAlign: 'center',
  },
  msiLabel: {
    fontSize: isMobile ? '14px' : '16px',
    color: '#888',
    textAlign: 'center',
    marginTop: '8px',
  },
  msiCondition: {
    fontSize: isMobile ? '16px' : '20px',
    fontWeight: 'bold',
    textAlign: 'center',
    marginTop: '12px',
    padding: '8px 16px',
    borderRadius: '8px',
    display: 'inline-block',
  },
  chartContainer: {
    backgroundColor: '#1a1a2e',
    borderRadius: '12px',
    padding: isMobile ? '12px' : '16px',
    marginTop: '16px',
  },
  rankingList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  rankingItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    backgroundColor: '#252540',
    borderRadius: '8px',
  },
  rankingRank: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    fontSize: '14px',
  },
  rankingInfo: {
    flex: 1,
  },
  rankingName: {
    color: '#fff',
    fontSize: isMobile ? '14px' : '16px',
    fontWeight: 'bold',
  },
  rankingAttribute: {
    color: '#888',
    fontSize: '12px',
  },
  rankingChange: {
    fontSize: isMobile ? '14px' : '16px',
    fontWeight: 'bold',
  },
});

// ========================================
// サブコンポーネント
// ========================================

/**
 * スパークライン表示
 */
const Sparkline = ({ data, color = '#4dabf7', isMobile }) => {
  if (!data || data.length === 0) {
    return <div style={{ color: '#666', fontSize: '12px' }}>データなし</div>;
  }

  return (
    <div style={createStyles(isMobile).sparkline}>
      {data.map((value, index) => (
        <div
          key={index}
          style={{
            ...createStyles(isMobile).sparklineBar,
            height: `${Math.max(value, 5)}%`,
            backgroundColor: color,
          }}
        />
      ))}
    </div>
  );
};

/**
 * 永続トレンドタブ
 */
const PersistentTab = ({ persistentModifiers, isMobile }) => {
  const styles = createStyles(isMobile);

  // 永続変動がない場合
  if (!persistentModifiers) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#aaa' }}>
        <p>永続変動データがありません</p>
        <p style={{ fontSize: '14px', marginTop: '8px' }}>
          対戦を重ねることで、デイリーニュースの10%が永続的なトレンドとして蓄積されます
        </p>
      </div>
    );
  }

  // ソート用ヘルパー（値が大きい順）
  const sortByAbsValue = (entries) => {
    return [...entries].sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]));
  };

  // 変動値の表示色
  const getValueColor = (value) => {
    if (value > 10) return '#4caf50';
    if (value > 0) return '#8bc34a';
    if (value < -10) return '#f44336';
    if (value < 0) return '#ff9800';
    return '#888';
  };

  // 変動バーの幅（最大100%）
  const getBarWidth = (value) => {
    return Math.min(Math.abs(value), 100);
  };

  const attributeEntries = sortByAbsValue(Object.entries(persistentModifiers.attributes || {}));
  const categoryEntries = sortByAbsValue(Object.entries(persistentModifiers.categories || {}))
    .filter(([, v]) => Math.abs(v) >= 0.1); // 微小な値は表示しない

  return (
    <div>
      {/* 説明 */}
      <div style={{
        ...styles.card,
        marginBottom: '16px',
        padding: '16px',
        backgroundColor: 'rgba(255, 215, 0, 0.1)',
        border: '1px solid rgba(255, 215, 0, 0.3)',
      }}>
        <div style={{ fontSize: '14px', color: '#ffd700', marginBottom: '8px' }}>
          📈 永続トレンドとは？
        </div>
        <div style={{ fontSize: '13px', color: '#ccc', lineHeight: '1.6' }}>
          デイリーニュースの変動の10%が毎日蓄積され、長期的な市場トレンドを形成します。
          <br />
          ・大きく変動した属性・カテゴリは徐々に平均に回帰します（回帰圧力）
          <br />
          ・上限はありませんが、±50%を超えると回帰が始まります
        </div>
      </div>

      {/* 属性別永続変動 */}
      <div style={{ ...styles.card, marginBottom: '16px' }}>
        <h3 style={{ fontSize: '16px', marginBottom: '12px', color: '#ffd700' }}>
          🔮 属性別永続変動
        </h3>
        {attributeEntries.map(([attr, value]) => (
          <div key={attr} style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '8px',
            padding: '8px 12px',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            borderRadius: '4px',
          }}>
            <span style={{
              width: '60px',
              color: ATTRIBUTE_COLORS[attr] || '#888',
              fontWeight: 'bold',
            }}>
              {attr}
            </span>
            <div style={{
              flex: 1,
              height: '20px',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '4px',
              overflow: 'hidden',
              position: 'relative',
            }}>
              <div style={{
                position: 'absolute',
                left: value >= 0 ? '50%' : `${50 - getBarWidth(value) / 2}%`,
                width: `${getBarWidth(value) / 2}%`,
                height: '100%',
                backgroundColor: getValueColor(value),
                opacity: 0.7,
              }} />
              <div style={{
                position: 'absolute',
                left: '50%',
                width: '1px',
                height: '100%',
                backgroundColor: '#666',
              }} />
            </div>
            <span style={{
              width: '80px',
              textAlign: 'right',
              color: getValueColor(value),
              fontWeight: 'bold',
              fontSize: '14px',
            }}>
              {value > 0 ? '+' : ''}{value.toFixed(1)}%
            </span>
          </div>
        ))}
      </div>

      {/* カテゴリ別永続変動（変動があるもののみ） */}
      {categoryEntries.length > 0 && (
        <div style={styles.card}>
          <h3 style={{ fontSize: '16px', marginBottom: '12px', color: '#ffd700' }}>
            🏷️ カテゴリ別永続変動（上位）
          </h3>
          {categoryEntries.slice(0, 15).map(([cat, value]) => (
            <div key={cat} style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '6px 12px',
              marginBottom: '4px',
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              borderRadius: '4px',
            }}>
              <span style={{ color: '#ccc' }}>【{cat}】</span>
              <span style={{
                color: getValueColor(value),
                fontWeight: 'bold',
              }}>
                {value > 0 ? '+' : ''}{value.toFixed(1)}%
              </span>
            </div>
          ))}
          {categoryEntries.length > 15 && (
            <div style={{ textAlign: 'center', color: '#888', fontSize: '12px', marginTop: '8px' }}>
              他 {categoryEntries.length - 15} カテゴリ
            </div>
          )}
        </div>
      )}

      {categoryEntries.length === 0 && (
        <div style={{ ...styles.card, textAlign: 'center', color: '#888' }}>
          <p>カテゴリ別の永続変動はまだありません</p>
        </div>
      )}
    </div>
  );
};

/**
 * 総合タブ
 */
const OverviewTab = ({ priceHistory, isMobile }) => {
  const styles = createStyles(isMobile);
  const msiData = getMarketIndexChartData(priceHistory);

  const getConditionStyle = (condition) => {
    const colors = {
      '好況': { bg: '#2e7d32', color: '#fff' },
      'やや好況': { bg: '#4caf50', color: '#fff' },
      '安定': { bg: '#757575', color: '#fff' },
      'やや不況': { bg: '#ff9800', color: '#000' },
      '不況': { bg: '#d32f2f', color: '#fff' },
    };
    return colors[condition] || colors['安定'];
  };

  const conditionStyle = getConditionStyle(msiData.marketCondition);

  return (
    <div>
      {/* MSI表示 */}
      <div style={styles.msiDisplay}>
        <div style={styles.msiValue}>
          {msiData.currentPrice > 0 ? msiData.currentPrice.toLocaleString() : '---'}
        </div>
        <div style={styles.msiLabel}>Magic Spirit Index (MSI)</div>
        <div style={{ textAlign: 'center', marginTop: '12px' }}>
          <span
            style={{
              ...styles.msiCondition,
              backgroundColor: conditionStyle.bg,
              color: conditionStyle.color,
            }}
          >
            {msiData.marketCondition}
          </span>
        </div>
        <div style={{ textAlign: 'center', marginTop: '12px' }}>
          <span style={{ color: getTrendColor(msiData.changePercent), fontSize: '18px' }}>
            {getTrendIcon(msiData.changePercent)} {msiData.changePercent > 0 ? '+' : ''}{msiData.changePercent}%
          </span>
        </div>
      </div>

      {/* MSIチャート */}
      {msiData.prices.length > 0 && (
        <div style={styles.chartContainer}>
          <PriceChart
            chartData={msiData}
            title="MSI推移（30戦）"
          />
        </div>
      )}

      {/* 属性サマリー */}
      <div style={{ ...styles.section, marginTop: '24px' }}>
        <h3 style={styles.sectionTitle}>🔮 属性別サマリー</h3>
        <div style={styles.grid}>
          {ATTRIBUTES.map((attr) => {
            const data = getAttributeChartData(priceHistory, attr);
            const sparkData = generateSparklineData(data.prices);

            return (
              <div key={attr} style={styles.card}>
                <div style={styles.cardHeader}>
                  <span
                    style={{
                      ...styles.cardTitle,
                      color: ATTRIBUTE_COLORS[attr],
                    }}
                  >
                    {attr}属性
                  </span>
                  <span
                    style={{
                      ...styles.badge,
                      backgroundColor: getTrendColor(data.changePercent),
                      color: '#000',
                    }}
                  >
                    {data.changePercent > 0 ? '+' : ''}{data.changePercent}%
                  </span>
                </div>
                <Sparkline data={sparkData} color={ATTRIBUTE_COLORS[attr]} isMobile={isMobile} />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/**
 * 属性タブ
 */
const AttributesTab = ({ priceHistory, serverIndexes, isMobile }) => {
  const styles = createStyles(isMobile);
  const [selectedAttr, setSelectedAttr] = useState(ATTRIBUTES[0]);

  const historyData = getAttributeChartData(priceHistory, selectedAttr);

  // 履歴データがない場合はサーバーインデックスを使用
  const hasHistory = historyData.prices.length > 0;
  const serverIndex = serverIndexes?.[selectedAttr] || 100;
  // インデックスは100が基準、変動率は(index - 100)で計算
  const serverChangePercent = Math.round((serverIndex - 100) * 10) / 10;

  const data = hasHistory ? historyData : {
    ...historyData,
    currentPrice: serverIndex,
    changePercent: serverChangePercent,
  };

  return (
    <div>
      {/* 属性選択 */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px' }}>
        {ATTRIBUTES.map((attr) => {
          const attrIndex = serverIndexes?.[attr] || 100;
          const attrChange = Math.round((attrIndex - 100) * 10) / 10;
          return (
            <button
              key={attr}
              onClick={() => setSelectedAttr(attr)}
              style={{
                ...styles.tab,
                ...(selectedAttr === attr ? styles.tabActive : styles.tabInactive),
                backgroundColor: selectedAttr === attr ? ATTRIBUTE_COLORS[attr] : '#333',
                position: 'relative',
              }}
            >
              {attr}
              <span style={{
                fontSize: '10px',
                marginLeft: '4px',
                color: attrChange > 0 ? '#4caf50' : attrChange < 0 ? '#f44336' : '#888',
              }}>
                {attrChange > 0 ? '+' : ''}{attrChange}%
              </span>
            </button>
          );
        })}
      </div>

      {/* 詳細データ */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <span style={{ ...styles.cardTitle, color: ATTRIBUTE_COLORS[selectedAttr] }}>
            {selectedAttr}属性 詳細分析
          </span>
          <span
            style={{
              ...styles.badge,
              backgroundColor: getTrendColor(data.changePercent),
              color: '#000',
            }}
          >
            {getTrendIcon(data.changePercent)} {data.changePercent > 0 ? '+' : ''}{data.changePercent}%
          </span>
        </div>

        {hasHistory ? (
          <>
            <div style={styles.statRow}>
              <span style={styles.statLabel}>現在平均</span>
              <span style={styles.statValue}>{data.currentPrice.toLocaleString()} G</span>
            </div>
            <div style={styles.statRow}>
              <span style={styles.statLabel}>最高値</span>
              <span style={{ ...styles.statValue, color: '#4caf50' }}>{data.highPrice.toLocaleString()} G</span>
            </div>
            <div style={styles.statRow}>
              <span style={styles.statLabel}>最安値</span>
              <span style={{ ...styles.statValue, color: '#f44336' }}>{data.lowPrice.toLocaleString()} G</span>
            </div>

            <div style={{ marginTop: '16px' }}>
              <PriceChart
                chartData={data}
                title={`${selectedAttr}属性 価格推移`}
              />
            </div>
          </>
        ) : (
          <>
            <div style={styles.statRow}>
              <span style={styles.statLabel}>現在指数</span>
              <span style={styles.statValue}>{serverIndex.toFixed(1)}</span>
            </div>
            <div style={styles.statRow}>
              <span style={styles.statLabel}>基準値からの変動</span>
              <span style={{
                ...styles.statValue,
                color: serverChangePercent > 0 ? '#4caf50' : serverChangePercent < 0 ? '#f44336' : '#888',
              }}>
                {serverChangePercent > 0 ? '+' : ''}{serverChangePercent}%
              </span>
            </div>
            <div style={{ marginTop: '16px', color: '#888', fontSize: '12px', textAlign: 'center' }}>
              履歴データはプレイを続けると記録されます
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/**
 * カテゴリタブ
 */
const CategoriesTab = ({ priceHistory, serverPrices, allCards, isMobile }) => {
  const styles = createStyles(isMobile);

  // サーバー価格からカテゴリ別平均を計算
  const serverCategoryData = useMemo(() => {
    if (!serverPrices || !allCards || Object.keys(serverPrices).length === 0) {
      return {};
    }

    const categoryData = {};
    for (const cat of CATEGORIES) {
      const catCards = allCards.filter(card =>
        card.category && card.category.includes(`【${cat}】`)
      );

      if (catCards.length === 0) {
        categoryData[cat] = { avgPrice: 0, changePercent: 0 };
        continue;
      }

      let totalPrice = 0;
      let totalBase = 0;
      for (const card of catCards) {
        const price = serverPrices[card.id] || 0;
        // 基礎価格は一律100として計算（簡易版）
        const basePrice = 100;
        totalPrice += price;
        totalBase += basePrice;
      }

      const avgPrice = Math.round(totalPrice / catCards.length);
      // 変動率 = (現在平均 - 基礎平均) / 基礎平均 * 100
      const avgBase = totalBase / catCards.length;
      const changePercent = avgBase > 0 ? Math.round((avgPrice - avgBase) / avgBase * 100 * 10) / 10 : 0;

      categoryData[cat] = { avgPrice, changePercent };
    }

    return categoryData;
  }, [serverPrices, allCards]);

  // カテゴリを変動率でソート
  const sortedCategories = useMemo(() => {
    const hasHistory = Object.values(priceHistory.categories || {}).some(arr => arr.length > 0);

    return CATEGORIES
      .map((cat) => {
        if (hasHistory) {
          const data = getCategoryChartData(priceHistory, cat);
          return {
            name: cat,
            attribute: CATEGORY_ATTRIBUTES[cat] || '複数',
            ...data,
          };
        } else {
          // サーバーデータを使用
          const serverData = serverCategoryData[cat] || { avgPrice: 0, changePercent: 0 };
          return {
            name: cat,
            attribute: CATEGORY_ATTRIBUTES[cat] || '複数',
            currentPrice: serverData.avgPrice,
            changePercent: serverData.changePercent,
            prices: [],
            highPrice: serverData.avgPrice,
            lowPrice: serverData.avgPrice,
          };
        }
      })
      .sort((a, b) => b.changePercent - a.changePercent);
  }, [priceHistory, serverCategoryData]);

  const getRankColor = (index) => {
    if (index === 0) return '#ffd700';
    if (index === 1) return '#c0c0c0';
    if (index === 2) return '#cd7f32';
    return '#555';
  };

  return (
    <div>
      <h3 style={styles.sectionTitle}>🏷️ カテゴリランキング（変動率順）</h3>

      <div style={styles.rankingList}>
        {sortedCategories.map((cat, index) => (
          <div key={cat.name} style={styles.rankingItem}>
            <div
              style={{
                ...styles.rankingRank,
                backgroundColor: getRankColor(index),
                color: index < 3 ? '#000' : '#fff',
              }}
            >
              {index + 1}
            </div>
            <div style={styles.rankingInfo}>
              <div style={styles.rankingName}>【{cat.name}】</div>
              <div style={styles.rankingAttribute}>
                <span style={{ color: ATTRIBUTE_COLORS[cat.attribute] || '#888' }}>
                  {cat.attribute}属性
                </span>
                {' · '}
                <span>平均 {cat.currentPrice.toLocaleString()} G</span>
              </div>
            </div>
            <div
              style={{
                ...styles.rankingChange,
                color: getTrendColor(cat.changePercent),
              }}
            >
              {getTrendIcon(cat.changePercent)} {cat.changePercent > 0 ? '+' : ''}{cat.changePercent}%
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * ティアタブ
 */
const TiersTab = ({ priceHistory, serverPrices, allCards, cardValueMap, isMobile }) => {
  const styles = createStyles(isMobile);

  // サーバー価格からティア別平均を計算
  const serverTierData = useMemo(() => {
    if (!serverPrices || !allCards || Object.keys(serverPrices).length === 0) {
      return {};
    }

    const tierData = {};
    for (const tier of TIERS) {
      // カードをティアでフィルタ
      const tierCards = allCards.filter(card => {
        if (!cardValueMap) return false;
        const cardValue = cardValueMap.get(card.id);
        return cardValue && cardValue.tier === tier;
      });

      if (tierCards.length === 0) {
        tierData[tier] = { avgPrice: 0, changePercent: 0 };
        continue;
      }

      let totalPrice = 0;
      let totalBase = 0;
      for (const card of tierCards) {
        const price = serverPrices[card.id] || 0;
        const cardValue = cardValueMap?.get(card.id);
        const basePrice = cardValue?.baseValue || 100;
        totalPrice += price;
        totalBase += basePrice;
      }

      const avgPrice = Math.round(totalPrice / tierCards.length);
      const avgBase = totalBase / tierCards.length;
      const changePercent = avgBase > 0 ? Math.round((avgPrice - avgBase) / avgBase * 100 * 10) / 10 : 0;

      tierData[tier] = { avgPrice, changePercent };
    }

    return tierData;
  }, [serverPrices, allCards, cardValueMap]);

  const hasHistory = Object.values(priceHistory.tiers || {}).some(arr => arr.length > 0);

  return (
    <div>
      <h3 style={styles.sectionTitle}>⭐ ティア別分析</h3>

      <div style={styles.grid}>
        {TIERS.map((tier) => {
          let data;
          let sparkData;

          if (hasHistory) {
            data = getTierChartData(priceHistory, tier);
            sparkData = generateSparklineData(data.prices);
          } else {
            const serverData = serverTierData[tier] || { avgPrice: 0, changePercent: 0 };
            data = {
              currentPrice: serverData.avgPrice,
              changePercent: serverData.changePercent,
              prices: [],
              highPrice: serverData.avgPrice,
              lowPrice: serverData.avgPrice,
            };
            sparkData = [];
          }

          return (
            <div key={tier} style={styles.card}>
              <div style={styles.cardHeader}>
                <span
                  style={{
                    ...styles.cardTitle,
                    color: TIER_COLORS[tier],
                  }}
                >
                  ⭐ {tier}ティア
                </span>
                <span
                  style={{
                    ...styles.badge,
                    backgroundColor: getTrendColor(data.changePercent),
                    color: '#000',
                  }}
                >
                  {data.changePercent > 0 ? '+' : ''}{data.changePercent}%
                </span>
              </div>

              <div style={styles.statRow}>
                <span style={styles.statLabel}>現在平均</span>
                <span style={styles.statValue}>{data.currentPrice.toLocaleString()} G</span>
              </div>
              {hasHistory && (
                <>
                  <div style={styles.statRow}>
                    <span style={styles.statLabel}>最高値</span>
                    <span style={{ ...styles.statValue, color: '#4caf50' }}>{data.highPrice.toLocaleString()} G</span>
                  </div>
                  <div style={styles.statRow}>
                    <span style={styles.statLabel}>最安値</span>
                    <span style={{ ...styles.statValue, color: '#f44336' }}>{data.lowPrice.toLocaleString()} G</span>
                  </div>
                </>
              )}

              {sparkData.length > 0 ? (
                <div style={{ marginTop: '12px' }}>
                  <Sparkline data={sparkData} color={TIER_COLORS[tier]} isMobile={isMobile} />
                </div>
              ) : (
                <div style={{ marginTop: '12px', color: '#888', fontSize: '11px', textAlign: 'center' }}>
                  履歴なし
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * イベントタブ
 * サーバーから取得したニュースを表示
 */
const EventsTab = ({ priceHistory, overnightVolatility, spotlightEvent, activeTrend, chainNews, currentDay, serverNews, newsHistory, serverTimestamp, isMobile }) => {
  const styles = createStyles(isMobile);

  // 経過時間のフォーマット
  const formatTimeAgo = (date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffHour = Math.floor(diffMs / 3600000);

    if (diffMin < 60) {
      return `${diffMin}分前`;
    } else if (diffHour < 24) {
      return `${diffHour}時間前`;
    } else {
      const diffDay = Math.floor(diffHour / 24);
      return `${diffDay}日前`;
    }
  };

  // サーバーニュースのアイコン取得
  const getNewsIcon = (type) => {
    // サーバーニュースのtypeに基づいてアイコンを決定
    if (type === 'sudden' || type === 'sudden_event') return '⚡';
    if (type === 'overnight' || type === 'overnight_volatility') return '🌙';
    if (type === 'micro' || type === 'micro_fluctuation') return '📊';
    if (type === 'chain') return '🔗';
    // daily, major, 等は通常ニュース
    return '📰';
  };

  // サーバーニュースの修正値の色
  const getModifierColor = (modifier) => {
    if (modifier > 0) return '#4caf50';
    if (modifier < 0) return '#f44336';
    return '#888';
  };

  // タイムスタンプのフォーマット
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleString('ja-JP', {
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div>
      <h3 style={styles.sectionTitle}>📰 市場ニュース</h3>

      {/* サーバータイムスタンプ表示 */}
      {serverTimestamp && (
        <div style={{
          fontSize: '12px',
          color: '#888',
          marginBottom: '12px',
          textAlign: 'right',
        }}>
          最終更新: {formatTimestamp(serverTimestamp)}
        </div>
      )}

      {/* 週間トレンド */}
      {activeTrend && (
        <div style={{
          ...styles.card,
          marginBottom: '16px',
          padding: '16px',
          backgroundColor: 'rgba(255, 215, 0, 0.1)',
          border: '1px solid rgba(255, 215, 0, 0.3)',
          borderLeft: '3px solid #ffd700',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '8px',
          }}>
            <span style={{ fontSize: '18px' }}>📅</span>
            <span style={{ fontWeight: 'bold', color: '#ffd700' }}>
              [週間トレンド] {activeTrend.name}
            </span>
          </div>
          <div style={{ fontSize: '14px', color: '#e0e0e0' }}>
            {activeTrend.description}
          </div>
        </div>
      )}

      {/* スポットライトイベント */}
      {spotlightEvent && (
        <div style={{
          ...styles.card,
          marginBottom: '16px',
          padding: '16px',
          backgroundColor: 'rgba(255, 165, 0, 0.1)',
          border: '1px solid rgba(255, 165, 0, 0.3)',
          borderLeft: '3px solid #ffa500',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '8px',
          }}>
            <span style={{ fontSize: '18px' }}>🔦</span>
            <span style={{ fontWeight: 'bold', color: '#ffa500' }}>
              [スポットライト] {spotlightEvent.title}
            </span>
          </div>
          <div style={{ fontSize: '14px', color: '#e0e0e0', marginBottom: '4px' }}>
            {spotlightEvent.description}
          </div>
          <div style={{ fontSize: '12px', color: '#888' }}>
            対象: {spotlightEvent.cardName}（{spotlightEvent.cardAttribute}）
          </div>
        </div>
      )}

      {/* 夜間変動 */}
      {overnightVolatility && (
        <div style={{
          ...styles.card,
          marginBottom: '16px',
          padding: '16px',
          backgroundColor: overnightVolatility.direction === 'up'
            ? 'rgba(76, 175, 80, 0.15)'
            : 'rgba(244, 67, 54, 0.15)',
          border: `1px solid ${overnightVolatility.direction === 'up' ? 'rgba(76, 175, 80, 0.4)' : 'rgba(244, 67, 54, 0.4)'}`,
          borderLeft: `3px solid ${overnightVolatility.direction === 'up' ? '#4caf50' : '#f44336'}`,
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '8px',
          }}>
            <span style={{ fontSize: '18px' }}>🌙</span>
            <span style={{
              fontWeight: 'bold',
              color: overnightVolatility.direction === 'up' ? '#4caf50' : '#f44336',
            }}>
              [夜間変動]
            </span>
          </div>
          <div style={{ fontSize: '14px', color: '#e0e0e0', marginBottom: '8px' }}>
            {overnightVolatility.text}
          </div>
          <div style={{
            textAlign: 'right',
            fontWeight: 'bold',
            fontSize: '16px',
            color: overnightVolatility.direction === 'up' ? '#4caf50' : '#f44336',
          }}>
            {overnightVolatility.spikePercent > 0 ? '+' : ''}{overnightVolatility.spikePercent}%
          </div>
        </div>
      )}

      {/* 連鎖ニュース */}
      {chainNews && chainNews.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <h4 style={{ ...styles.sectionTitle, fontSize: '16px', marginBottom: '8px' }}>
            🔗 連鎖効果
          </h4>
          <div style={styles.eventList}>
            {chainNews.map((chain, index) => (
              <div key={index} style={{
                ...styles.eventItem,
                backgroundColor: 'rgba(147, 112, 219, 0.1)',
                borderLeft: '3px solid #9370db',
              }}>
                <div style={styles.eventIcon}>🔗</div>
                <div style={styles.eventContent}>
                  <div style={{
                    ...styles.eventType,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}>
                    <span>{chain.target || chain.chainType}</span>
                    {chain.modifier !== undefined && chain.modifier !== 0 && (
                      <span style={{
                        color: getModifierColor(chain.modifier),
                        fontWeight: 'bold',
                        fontSize: '12px',
                      }}>
                        {chain.modifier > 0 ? '+' : ''}{chain.modifier}%
                      </span>
                    )}
                  </div>
                  <div style={styles.eventText}>{chain.text}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* サーバーニュース一覧 */}
      {(!serverNews || serverNews.length === 0) && !overnightVolatility && !spotlightEvent && !activeTrend && (!chainNews || chainNews.length === 0) ? (
        <div style={styles.noData}>
          ニュースがありません
        </div>
      ) : serverNews && serverNews.length > 0 ? (
        <div>
          <h4 style={{ ...styles.sectionTitle, fontSize: '16px', marginBottom: '8px' }}>
            📰 本日のニュース
          </h4>
          <div style={styles.eventList}>
            {serverNews.map((news, index) => (
              <div key={index} style={styles.eventItem}>
                <div style={styles.eventIcon}>{getNewsIcon(news.type)}</div>
                <div style={styles.eventContent}>
                  <div style={{
                    ...styles.eventType,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}>
                    <span>{news.target || news.type}</span>
                    {news.modifier !== undefined && news.modifier !== 0 && (
                      <span style={{
                        color: getModifierColor(news.modifier),
                        fontWeight: 'bold',
                        fontSize: '12px',
                      }}>
                        {news.modifier > 0 ? '+' : ''}{news.modifier}%
                      </span>
                    )}
                  </div>
                  <div style={styles.eventText}>{news.text}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* ニュース履歴 */}
      {newsHistory && newsHistory.length > 0 && (
        <div style={{ marginTop: '24px' }}>
          <h4 style={{ ...styles.sectionTitle, fontSize: '16px', marginBottom: '12px' }}>
            📜 過去のニュース
          </h4>
          <div style={styles.eventList}>
            {newsHistory.slice(0, 10).map((entry, historyIndex) => {
              const mainNews = entry.news?.[0];
              if (!mainNews) return null;
              const historyDate = entry.timestamp ? new Date(entry.timestamp) : null;
              const timeAgo = historyDate ? formatTimeAgo(historyDate) : '';
              const isDaily = entry.updateType === 'daily';
              const hasChainNews = entry.chainNews && entry.chainNews.length > 0;
              const hasTrend = entry.trend;

              return (
                <div key={`history-${historyIndex}`} style={{
                  ...styles.eventItem,
                  opacity: 0.85,
                  borderLeft: `3px solid ${isDaily ? '#6b9eff' : '#ff6b9e'}`,
                  marginBottom: '8px',
                  backgroundColor: 'rgba(0,0,0,0.2)',
                }}>
                  <div style={{ ...styles.eventIcon, color: isDaily ? '#6b9eff' : '#ff6b9e' }}>
                    {isDaily ? '📅' : '📣'}
                  </div>
                  <div style={styles.eventContent}>
                    <div style={{
                      ...styles.eventType,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}>
                      <span style={{ color: '#888', fontSize: '11px' }}>
                        [{isDaily ? 'デイリー' : 'メジャー'}] {timeAgo}
                        {hasTrend && <span style={{ marginLeft: '8px', color: '#ffd700' }}>📈 {entry.trend.name}</span>}
                      </span>
                      {mainNews.modifier !== undefined && mainNews.modifier !== 0 && (
                        <span style={{
                          color: getModifierColor(mainNews.modifier),
                          fontWeight: 'bold',
                          fontSize: '11px',
                          opacity: 0.8,
                        }}>
                          {mainNews.modifier > 0 ? '+' : ''}{mainNews.modifier}%
                        </span>
                      )}
                    </div>
                    <div style={{ ...styles.eventText, fontSize: '13px' }}>{mainNews.text}</div>
                    {/* 追加ニュースがある場合 */}
                    {entry.news.length > 1 && (
                      <div style={{ marginTop: '4px', paddingLeft: '8px', borderLeft: '2px solid rgba(255,255,255,0.1)' }}>
                        {entry.news.slice(1, 3).map((subNews, subIndex) => (
                          <div key={`sub-${subIndex}`} style={{ fontSize: '11px', color: '#aaa', marginTop: '2px' }}>
                            • {subNews.text}
                          </div>
                        ))}
                        {entry.news.length > 3 && (
                          <div style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>
                            +{entry.news.length - 3} more
                          </div>
                        )}
                      </div>
                    )}
                    {/* 連鎖ニュースがある場合 */}
                    {hasChainNews && (
                      <div style={{ marginTop: '4px', paddingLeft: '8px', borderLeft: '2px solid rgba(107,158,255,0.3)' }}>
                        {entry.chainNews.slice(0, 2).map((chain, chainIndex) => (
                          <div key={`chain-${chainIndex}`} style={{ fontSize: '11px', color: '#6b9eff', marginTop: '2px' }}>
                            🔗 {chain.text}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * 資産タブ
 */
const AssetsTab = ({ assetHistory, isMobile }) => {
  const styles = createStyles(isMobile);
  const [displayMode, setDisplayMode] = useState('totalMarket');

  const DISPLAY_MODES = [
    { id: 'totalMarket', label: '総資産（市場）' },
    { id: 'totalBase', label: '総資産（基礎）' },
    { id: 'gold', label: '所持金' },
    { id: 'cardMarket', label: 'カード価値（市場）' },
    { id: 'cardBase', label: 'カード価値（基礎）' },
    { id: 'investment', label: '投資評価額' },
    { id: 'fund', label: 'ファンド' },
    { id: 'fx', label: 'FX' },
  ];

  const chartData = getAssetChartData(assetHistory, displayMode);
  const breakdown = getAssetBreakdown(assetHistory);
  const hasData = (assetHistory?.history || []).length > 0;

  // スパークラインデータ生成
  const sparkData = useMemo(() => {
    if (!chartData.prices || chartData.prices.length === 0) return [];
    const max = Math.max(...chartData.prices);
    const min = Math.min(...chartData.prices);
    const range = max - min || 1;
    return chartData.prices.map((v) => ((v - min) / range) * 100);
  }, [chartData.prices]);

  return (
    <div>
      {/* 総資産サマリー */}
      <div style={styles.msiDisplay}>
        <div style={styles.msiValue}>
          {breakdown.totalMarket > 0 ? breakdown.totalMarket.toLocaleString() : '---'} G
        </div>
        <div style={styles.msiLabel}>総資産（市場価格ベース）</div>
        {chartData.changePercent !== 0 && (
          <div style={{ textAlign: 'center', marginTop: '12px' }}>
            <span style={{ color: getTrendColor(chartData.changePercent), fontSize: '18px' }}>
              {getTrendIcon(chartData.changePercent)} {chartData.changePercent > 0 ? '+' : ''}{chartData.changePercent}%
            </span>
          </div>
        )}
      </div>

      {/* 内訳カード */}
      <div style={{ ...styles.section, marginBottom: '24px' }}>
        <h3 style={styles.sectionTitle}>📊 資産内訳</h3>
        <div style={styles.grid}>
          {/* 所持金 */}
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <span style={styles.cardTitle}>💰 所持金</span>
              <span
                style={{
                  ...styles.badge,
                  backgroundColor: '#ffd700',
                  color: '#000',
                }}
              >
                {breakdown.goldPercent}%
              </span>
            </div>
            <div style={{ fontSize: isMobile ? '20px' : '24px', fontWeight: 'bold', color: '#ffd700' }}>
              {breakdown.gold.toLocaleString()} G
            </div>
          </div>

          {/* カード価値（市場） */}
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <span style={styles.cardTitle}>🃏 カード価値（市場）</span>
              <span
                style={{
                  ...styles.badge,
                  backgroundColor: '#4dabf7',
                  color: '#000',
                }}
              >
                {breakdown.cardPercent}%
              </span>
            </div>
            <div style={{ fontSize: isMobile ? '20px' : '24px', fontWeight: 'bold', color: '#4dabf7' }}>
              {breakdown.cardValueMarket.toLocaleString()} G
            </div>
            <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
              基礎: {breakdown.cardValueBase.toLocaleString()} G
            </div>
          </div>

          {/* 投資評価額（投資がある場合のみ表示） */}
          {breakdown.investmentValue > 0 && (
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <span style={styles.cardTitle}>📈 投資評価額</span>
                <span
                  style={{
                    ...styles.badge,
                    backgroundColor: '#a855f7',
                    color: '#fff',
                  }}
                >
                  {breakdown.investmentPercent}%
                </span>
              </div>
              <div style={{ fontSize: isMobile ? '20px' : '24px', fontWeight: 'bold', color: '#a855f7' }}>
                {breakdown.investmentValue.toLocaleString()} G
              </div>
              <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
                ファンド: {(breakdown.fundValue || 0).toLocaleString()} G / FX: {(breakdown.fxValue || 0).toLocaleString()} G
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 表示モード選択 */}
      <div style={{ ...styles.section, marginBottom: '16px' }}>
        <h3 style={styles.sectionTitle}>📈 推移チャート</h3>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
          {DISPLAY_MODES.map((mode) => (
            <button
              key={mode.id}
              onClick={() => setDisplayMode(mode.id)}
              style={{
                ...styles.tab,
                ...(displayMode === mode.id ? styles.tabActive : styles.tabInactive),
              }}
            >
              {mode.label}
            </button>
          ))}
        </div>

        {/* チャート */}
        {hasData ? (
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <span style={styles.cardTitle}>{getAssetModeLabel(displayMode)}</span>
              <span
                style={{
                  ...styles.badge,
                  backgroundColor: getTrendColor(chartData.changePercent),
                  color: '#000',
                }}
              >
                {chartData.changePercent > 0 ? '+' : ''}{chartData.changePercent}%
              </span>
            </div>

            <div style={styles.statRow}>
              <span style={styles.statLabel}>現在</span>
              <span style={styles.statValue}>{chartData.currentPrice.toLocaleString()} G</span>
            </div>
            <div style={styles.statRow}>
              <span style={styles.statLabel}>最高</span>
              <span style={{ ...styles.statValue, color: '#4caf50' }}>{chartData.highPrice.toLocaleString()} G</span>
            </div>
            <div style={styles.statRow}>
              <span style={styles.statLabel}>最安</span>
              <span style={{ ...styles.statValue, color: '#f44336' }}>{chartData.lowPrice.toLocaleString()} G</span>
            </div>
            <div style={styles.statRow}>
              <span style={styles.statLabel}>変動額</span>
              <span style={{ ...styles.statValue, color: getTrendColor(chartData.changeAmount) }}>
                {chartData.changeAmount >= 0 ? '+' : ''}{chartData.changeAmount.toLocaleString()} G
              </span>
            </div>

            {chartData.prices.length > 0 && (
              <div style={{ marginTop: '16px' }}>
                <Sparkline data={sparkData} color="#ffd700" isMobile={isMobile} />
              </div>
            )}
          </div>
        ) : (
          <div style={styles.noData}>
            <div>資産履歴がありません</div>
            <div style={{ fontSize: '12px', marginTop: '8px', color: '#666' }}>
              対戦を行うと資産データが蓄積されます
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * 検索タブ
 */
const SearchTab = ({ priceHistory, allCards, isMobile, onCardSelect }) => {
  const styles = createStyles(isMobile);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCard, setSelectedCard] = useState(null);

  const filteredCards = useMemo(() => {
    if (!searchQuery || searchQuery.length < 2) return [];
    const query = searchQuery.toLowerCase();
    return (allCards || [])
      .filter(
        (card) => {
          // card.categoryは文字列または配列の可能性がある
          const categoryStr = Array.isArray(card.category)
            ? card.category.join(' ')
            : (card.category || '');
          return card.name?.toLowerCase().includes(query) ||
            categoryStr.toLowerCase().includes(query);
        }
      )
      .slice(0, 20);
  }, [searchQuery, allCards]);

  const handleCardClick = (card) => {
    setSelectedCard(card);
    if (onCardSelect) {
      onCardSelect(card);
    }
  };

  const cardData = selectedCard
    ? getCardChartData(priceHistory, selectedCard.id, 1)
    : null;

  return (
    <div>
      <h3 style={styles.sectionTitle}>🔍 カード検索</h3>

      <input
        type="text"
        placeholder="カード名またはカテゴリで検索..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        style={styles.searchInput}
      />

      {selectedCard && cardData ? (
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <span style={styles.cardTitle}>
              <span style={{ color: ATTRIBUTE_COLORS[selectedCard.attribute] || '#fff' }}>
                {selectedCard.name}
              </span>
            </span>
            <button
              onClick={() => setSelectedCard(null)}
              style={{
                background: 'none',
                border: 'none',
                color: '#888',
                cursor: 'pointer',
                fontSize: '18px',
              }}
            >
              ✕
            </button>
          </div>

          <div style={styles.statRow}>
            <span style={styles.statLabel}>属性</span>
            <span style={{ ...styles.statValue, color: ATTRIBUTE_COLORS[selectedCard.attribute] }}>
              {selectedCard.attribute}
            </span>
          </div>
          <div style={styles.statRow}>
            <span style={styles.statLabel}>カテゴリ</span>
            <span style={styles.statValue}>{selectedCard.category || '-'}</span>
          </div>
          <div style={styles.statRow}>
            <span style={styles.statLabel}>現在価格</span>
            <span style={styles.statValue}>{cardData.currentPrice.toLocaleString()} G</span>
          </div>
          <div style={styles.statRow}>
            <span style={styles.statLabel}>変動率</span>
            <span style={{ ...styles.statValue, color: getTrendColor(cardData.changePercent) }}>
              {getTrendIcon(cardData.changePercent)} {cardData.changePercent > 0 ? '+' : ''}{cardData.changePercent}%
            </span>
          </div>

          {cardData.prices.length > 0 && (
            <div style={{ marginTop: '16px' }}>
              <PriceChart
                chartData={cardData}
                title="価格推移"
              />
            </div>
          )}
        </div>
      ) : (
        <div style={styles.searchResults}>
          {filteredCards.length > 0 ? (
            filteredCards.map((card) => (
              <div
                key={card.id}
                style={styles.searchResultItem}
                onClick={() => handleCardClick(card)}
              >
                <div>
                  <div style={{ color: ATTRIBUTE_COLORS[card.attribute] || '#fff', fontWeight: 'bold' }}>
                    {card.name}
                  </div>
                  <div style={{ color: '#888', fontSize: '12px' }}>
                    {card.attribute} · {card.category || '-'}
                  </div>
                </div>
                <div style={{ color: '#888' }}>→</div>
              </div>
            ))
          ) : searchQuery.length >= 2 ? (
            <div style={styles.noData}>該当するカードが見つかりません</div>
          ) : (
            <div style={styles.noData}>2文字以上入力してください</div>
          )}
        </div>
      )}
    </div>
  );
};

// ========================================
// メインコンポーネント
// ========================================

/**
 * 市場分析画面
 *
 * @param {Object} props
 * @param {Object} props.marketState - 市場状態
 * @param {Object[]} props.allCards - 全カードデータ
 * @param {Object} props.assetHistory - 資産履歴（オプション）
 * @param {Map} props.cardValueMap - カード価値マップ（オプション）
 * @param {Function} props.onClose - 閉じるコールバック
 * @param {Function} props.onCardSelect - カード選択コールバック（オプション）
 */
const MarketAnalysis = ({ marketState, allCards, assetHistory, cardValueMap, onClose, onCardSelect }) => {
  const isMobile = useIsMobile();
  const styles = createStyles(isMobile);
  const [activeTab, setActiveTab] = useState('overview');

  const priceHistory = marketState?.priceHistory || {
    cards: {},
    attributes: {},
    categories: {},
    tiers: {},
    marketIndex: [],
    events: [],
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return <OverviewTab priceHistory={priceHistory} isMobile={isMobile} />;
      case 'persistent':
        return <PersistentTab persistentModifiers={marketState?.persistentModifiers} isMobile={isMobile} />;
      case 'attributes':
        return <AttributesTab priceHistory={priceHistory} serverIndexes={marketState?.serverIndexes} isMobile={isMobile} />;
      case 'categories':
        return <CategoriesTab priceHistory={priceHistory} serverPrices={marketState?.cardPrices} allCards={allCards} isMobile={isMobile} />;
      case 'tiers':
        return <TiersTab priceHistory={priceHistory} serverPrices={marketState?.cardPrices} allCards={allCards} cardValueMap={cardValueMap} isMobile={isMobile} />;
      case 'assets':
        return <AssetsTab assetHistory={assetHistory} isMobile={isMobile} />;
      case 'events':
        return (
          <EventsTab
            priceHistory={priceHistory}
            overnightVolatility={marketState?.serverOvernightVolatility}
            spotlightEvent={marketState?.serverSpotlightEvent}
            activeTrend={marketState?.serverActiveTrend}
            chainNews={marketState?.serverChainNews}
            currentDay={marketState?.currentDay}
            serverNews={marketState?.serverNews}
            newsHistory={marketState?.serverNewsHistory}
            serverTimestamp={marketState?.serverTimestamp}
            isMobile={isMobile}
          />
        );
      case 'search':
        return (
          <SearchTab
            priceHistory={priceHistory}
            allCards={allCards}
            isMobile={isMobile}
            onCardSelect={onCardSelect}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div style={styles.container}>
      {/* ヘッダー */}
      <div style={styles.header}>
        <div style={styles.title}>
          📊 市場分析
        </div>
        <button style={styles.closeButton} onClick={onClose}>
          ✕
        </button>
      </div>

      {/* タブバー */}
      <div style={styles.tabBar}>
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              ...styles.tab,
              ...(activeTab === tab.id ? styles.tabActive : styles.tabInactive),
            }}
          >
            {tab.icon} {!isMobile && tab.label}
          </button>
        ))}
      </div>

      {/* コンテンツ */}
      <div style={styles.content}>
        <div style={styles.contentInner}>{renderTabContent()}</div>
      </div>
    </div>
  );
};

export default MarketAnalysis;
